import dayjs from "dayjs"
import { useTheme } from "styled-components"

import { cn } from "@forento/shared-client/utilities/tailwind"

import { usePlatform } from "~/contexts/PlatformContext"
import useMonthNavigation from "~/hooks/useMonthNavigation"
import Button from "~/themes/school/components/Button"
import { ChevronIcon, ClockIcon } from "~/themes/school/components/Icon"
import { useTranslation } from "~/translations"
import { isColorDark } from "~/utilities/color"
import { query } from "~/utilities/trpc"

export const Calendar = () => {
	const platform = usePlatform().platform
	const t = useTranslation()
	const theme = useTheme()

	const calendar = useMonthNavigation()

	const events = query.event.listPublic.useQuery()

	const firstDayOfMonth = dayjs(`${calendar.year}-${calendar.month + 1}-01`)
	const daysInMonth = firstDayOfMonth.daysInMonth()
	// Convert Sunday (0) to 6, and other days to 0-5, effectively making Monday (1) the first day
	const startDayOfWeek = (firstDayOfMonth.day() + 6) % 7

	const prevMonth = firstDayOfMonth.subtract(1, "month")
	const daysInPrevMonth = prevMonth.daysInMonth()

	const weekDays = [
		t("dashboard.weekDays.monday"),
		t("dashboard.weekDays.tuesday"),
		t("dashboard.weekDays.wednesday"),
		t("dashboard.weekDays.thursday"),
		t("dashboard.weekDays.friday"),
		t("dashboard.weekDays.saturday"),
		t("dashboard.weekDays.sunday"),
	]

	return (
		<div className="bg-white p-6 shadow">
			<div className="mb-6 flex items-center justify-between">
				<Button
					onClick={calendar.setPrevious}
					isDisabled={calendar.year === dayjs().year() && calendar.month === dayjs().month()}
					className="disabled:opacity-25"
				>
					<ChevronIcon direction="left" className="h-6 w-6" />
				</Button>
				<h2 className="font-quicksand font-bold">
					{new Intl.DateTimeFormat(platform.language, {
						month: "long",
						year: "numeric",
					}).format(new Date(calendar.year, calendar.month, 1))}
				</h2>
				<Button onClick={calendar.setNext}>
					<ChevronIcon direction="right" className="h-6 w-6" />
				</Button>
			</div>
			<div className="grid grid-cols-7 grid-rows-5 gap-4">
				{weekDays.map(day => (
					<div key={day} className="text-center text-light">
						{day}
					</div>
				))}
				{Array.from({ length: 7 * 5 }).map((_, index) => {
					let date: number
					let monthOffset = 0

					if (index < startDayOfWeek) {
						date = daysInPrevMonth - startDayOfWeek + index + 1
						monthOffset = -1
					} else if (index >= startDayOfWeek + daysInMonth) {
						date = index - (startDayOfWeek + daysInMonth) + 1
						monthOffset = 1
					} else {
						date = index - startDayOfWeek + 1
					}

					const eventsThisDate =
						events.data?.filter(x => {
							const days = Array.from(
								{
									length: dayjs(x.endDate).subtract(1, "second").diff(dayjs(x.startDate), "day") + 1,
								},
								(_, i) => dayjs(x.startDate).add(i, "day"),
							)
							const currentDate = dayjs(new Date(calendar.year, calendar.month + monthOffset, date))
							return days.some(x => x.isSame(currentDate, "day"))
						}) ?? []

					const isToday =
						dayjs().month() === calendar.month &&
						dayjs().year() === calendar.year &&
						dayjs().date() === date

					return (
						<div
							key={`date-${index}`}
							className={cn(
								"group relative flex aspect-square items-center justify-center rounded-full",
								monthOffset === 0 ? "font-bold" : "text-light",
								isToday && "text-red-500",
								eventsThisDate?.length > 0 &&
									cn(
										"cursor-pointer bg-primary font-bold",
										isToday
											? "bg-red-500 text-white"
											: isColorDark(theme.primaryColor) && "text-white",
									),
							)}
						>
							{date}
							{eventsThisDate.length > 0 && (
								<div className="pointer-events-none absolute bottom-full left-1/2 mb-3 flex min-w-[200px] origin-bottom -translate-x-1/2 -translate-y-1 scale-0 flex-col gap-3 rounded-xl bg-gray-800 px-4 py-3 text-sm text-white shadow-xl transition-all duration-150 group-hover:scale-100">
									{eventsThisDate.map(event => {
										const isMultiDayEvent = !dayjs(event.startDate).isSame(
											dayjs(event.endDate).subtract(1, "second"),
											"day",
										)
										return (
											<div key={event.id}>
												<div className="text-base font-medium">{event.title}</div>
												<div className="mt-2 flex items-center gap-2 text-gray-300">
													<ClockIcon className="h-4 w-4" />
													{isMultiDayEvent ? (
														<div className="flex flex-col">
															<div>
																{new Intl.DateTimeFormat(platform.language, {
																	month: "long",
																	year: "numeric",
																}).format(event.startDate)}
															</div>
															<div className="flex items-center gap-1">
																<span className="text-gray-500">
																	{t("dashboard.dateToDate")}
																</span>
																{new Intl.DateTimeFormat(platform.language, {
																	month: "long",
																	year: "numeric",
																}).format(event.endDate)}
															</div>
														</div>
													) : (
														<>
															{dayjs(event.startDate).format("HH:mm")}&mdash;
															{dayjs(event.endDate).format("HH:mm")}
														</>
													)}
												</div>
											</div>
										)
									})}
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}
